import React, { useMemo, useState } from 'react';
import { track, Events } from '../analytics';
import Button from './Button';

// Builds a short, human "blurb" about a backtest run plus a deep link to it,
// then lets the user copy it, share it natively (mobile), or post it to X.
// Works for both logged-in and logged-out viewers — the /backtests/:id link is
// publicly viewable for owner-less (shared) runs.
function buildBlurb(backtest) {
  const r = backtest?.results || {};
  const sym = backtest?.symbol || '';
  const name = backtest?.strategy_name || 'A trading strategy';

  const parts = [];
  if (r.return_pct != null) {
    parts.push(`${r.return_pct >= 0 ? '+' : ''}${r.return_pct}% return`);
  }
  if (r.sharpe_ratio != null) parts.push(`${r.sharpe_ratio} Sharpe`);
  if (r.max_drawdown_pct != null) parts.push(`${r.max_drawdown_pct}% max drawdown`);
  if (r.total_trades != null) parts.push(`${r.total_trades} trades`);

  const stats = parts.length ? `: ${parts.join(', ')}` : '';
  const period = backtest?.start_date && backtest?.end_date
    ? ` (${backtest.start_date} → ${backtest.end_date})`
    : '';

  const headline = `📈 ${name}${sym ? ` on ${sym}` : ''}${stats}${period}.`;
  return `${headline}\n\nBacktested free on Algovex — build & test your own, no code:`;
}

export default function ShareBacktestModal({ backtest, onClose }) {
  const link = useMemo(
    () => `${window.location.origin}/backtests/${backtest?.id}`,
    [backtest?.id]
  );
  const defaultText = useMemo(() => `${buildBlurb(backtest)}\n${link}`, [backtest, link]);
  const [text, setText] = useState(defaultText);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      track(Events.BACKTEST_SHARE_COPIED, { backtest_id: backtest?.id });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API blocked (insecure context / permissions) — select the
      // textarea so the user can copy manually.
      const el = document.getElementById('share-blurb-textarea');
      if (el) { el.focus(); el.select(); }
    }
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({ text, url: link });
      track(Events.BACKTEST_SHARE_COPIED, { backtest_id: backtest?.id, method: 'native' });
    } catch {
      /* user cancelled or unsupported — no-op */
    }
  };

  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
        padding: 28, maxWidth: 520, width: '100%', position: 'relative', maxHeight: '85vh', overflowY: 'auto',
      }}>
        <button onClick={onClose} style={{
          position: 'absolute', top: 16, right: 16, background: 'none', border: 'none',
          color: 'var(--muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1,
        }}>✕</button>

        <h3 style={{ margin: '0 0 6px', color: 'var(--text)', fontSize: 18 }}>Share this backtest</h3>
        <p style={{ color: 'var(--muted)', margin: '0 0 16px', fontSize: 13, lineHeight: 1.6 }}>
          A short summary of the run with a link anyone can open. Edit the text if you like.
        </p>

        <textarea
          id="share-blurb-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          style={{
            width: '100%', boxSizing: 'border-box', resize: 'vertical',
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
            padding: '10px 12px', color: 'var(--text)', fontSize: 13, lineHeight: 1.6,
            fontFamily: 'var(--font-body)', marginBottom: 14,
          }}
        />

        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <Button variant="primary" size="sm" onClick={handleCopy}>
            {copied ? '✓ Copied' : 'Copy'}
          </Button>
          <a href={xUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <Button variant="secondary" size="sm" onClick={() => track(Events.BACKTEST_SHARE_COPIED, { backtest_id: backtest?.id, method: 'x' })}>
              Share on X
            </Button>
          </a>
          {canNativeShare && (
            <Button variant="secondary" size="sm" onClick={handleNativeShare}>Share…</Button>
          )}
        </div>
      </div>
    </div>
  );
}
